import { isProduction } from "@/services/buildMode";
import { deleteCookie, getCookie, setCookie } from "@/services/cookies";
import { CookieName, CookieRegistry } from "@/services/cookieRegistry";
import { IndexedDb, IndexedDbKey } from "@/services/indexedDb";
import type { UserToken } from "@/services/user/userTypes";

const { name: cookieName, expirationDays } = CookieRegistry[CookieName.AuthToken];

/**
 * Handles token storing and retrieval in/from browser cookie.
 *
 * Cookie storage is chosen over IndexedDB for forward-compatibility with Nuxt SSR:
 * the server can read the cookie on initial request via `useCookie()`, eliminating
 * the need for a client-side async token lookup on every page load.
 */
export const TokenService = {
  get: (): UserToken | null => {
    return getCookie(cookieName);
  },

  store: (token: UserToken): void => {
    const expiration = new Date();
    expiration.setDate(expiration.getDate() + expirationDays);
    setCookie(cookieName, token, { expiration: expiration.toUTCString(), path: "/", secure: isProduction });
  },

  delete: (): void => {
    deleteCookie(cookieName);
  },
};

/**
 * One-time migration: moves token from IndexedDB (old storage) to cookie (new storage).
 * Called once on app startup. Safe to call repeatedly — no-ops if nothing to migrate.
 * Can be removed once all users have been migrated (after a few releases).
 */
export async function migrateTokenFromIndexedDb(): Promise<void> {
  const indexedDbToken = await IndexedDb.get(IndexedDbKey.UserToken);
  if (!indexedDbToken) {
    return;
  }

  if (!TokenService.get()) {
    TokenService.store(indexedDbToken as UserToken);
  }

  await IndexedDb.delete(IndexedDbKey.UserToken);
}
